// Centralised Vuex cleanup. Called in two places:
//   1. Logout (router beforeEach for Login / ResetPassword) — alongside
//      AppModule.cleanState() which wipes auth + currentAgencyContext.
//   2. Agency switch (AppModule.getAgencyContext) — when a sysadmin moves
//      between agencies. Without this, cached agency-scoped data (rate
//      cards, location types, etc.) from the previous agency would leak
//      into the new one and cause foreign-key references to entities
//      from the wrong agency. This was the root cause of the Convert-
//      to-Won failure where the wizard picker held a Lime rate card
//      while the user was acting on Seismic Shift.
//
// AppModule itself is intentionally NOT cleared here — it owns the auth
// state and the currentAgencyContext we're about to switch to.

import { SysModule } from '@/store/modules/sys';
import { AgencyModule } from '@/store/modules/agency';
import { AgencySettingsModule } from '@/store/modules/agencySettings';
import { AgencyDashboardModule } from '@/store/modules/agencyDashboard';
import { AgencyScheduleModule } from '@/store/modules/agencySchedule';
import { BookingDetailsModule } from '@/store/modules/bookingDetails';
import { BookingListModule } from '@/store/modules/bookingList';
import { BookingWizardModule } from '@/store/modules/bookingWizard';
import { WorkerAnonModule } from '@/store/modules/workerAnon';
import { WorkerDetailsModule } from '@/store/modules/workerDetails';
import { WorkerListModule } from '@/store/modules/workerList';
import { WorkerWizardModule } from '@/store/modules/workerWizard';
import { CompanyDetailsModule } from '@/store/modules/companyDetails';
import { CompanyListModule } from '@/store/modules/companyList';
import { ComplianceModule } from '@/store/modules/compliance';
import { ContactDetailsModule } from '@/store/modules/contactDetails';
import { ContactListModule } from '@/store/modules/contactList';
import { ContactAnonModule } from '@/store/modules/contactAnon';
import { JobDetailsModule } from '@/store/modules/jobDetails';
import { JobListModule } from '@/store/modules/jobList';
import { CampaignDetailsModule } from '@/store/modules/campaignDetails';
import { CampaignListModule } from '@/store/modules/campaignList';
import { SpecOutDetailsModule } from '@/store/modules/specOutDetails';
import { SpecOutListModule } from '@/store/modules/specOutList';
import { LocationDetailsModule } from '@/store/modules/locationDetails';
import { LocationListModule } from '@/store/modules/locationList';
import { ReportsModule } from '@/store/modules/reports';
import { TimesheetListModule } from '@/store/modules/timesheetList';
import { SysKBContentListModule } from '@/store/modules/sysKBContentList';
import { OpportunityDetailsModule } from '@/store/modules/opportunityDetails';
import { OpportunitiesListModule } from '@/store/modules/opportunitiesList';
import { InactivityReportModule } from '@/store/modules/inactivityReport';
import { ManagementReportsModule } from '@/store/modules/managementReports';
import { ActivitiesListModule } from '@/store/modules/activitiesList';
import { ActivityTimelineModule } from '@/store/modules/activityTimeline';
import { InvoiceDetailsModule } from '@/store/modules/invoiceDetails';
import { InvoiceListModule } from '@/store/modules/invoiceList';
import { KpiTargetsModule } from '@/store/modules/kpiTargets';
import { AgencyKBModule } from '@/store/modules/agencyKB';
import { MessagingModule } from '@/store/modules/messaging';
import { PayrollRunListModule } from '@/store/modules/payrollRunList';
import { PayrollRunDetailsModule } from '@/store/modules/payrollRunDetails';

export function cleanAllAgencyScopedStores(): void {
  SysModule.cleanState();
  AgencyModule.cleanState();
  AgencySettingsModule.cleanState();
  AgencyDashboardModule.cleanState();
  AgencyScheduleModule.cleanState();
  BookingDetailsModule.cleanState();
  BookingListModule.cleanState();
  BookingWizardModule.cleanState();
  WorkerAnonModule.cleanState();
  WorkerDetailsModule.cleanState();
  WorkerListModule.cleanState();
  WorkerWizardModule.cleanState();
  CompanyDetailsModule.cleanState();
  CompanyListModule.cleanState();
  ComplianceModule.cleanState();
  ContactDetailsModule.cleanState();
  ContactListModule.cleanState();
  ContactAnonModule.cleanState();
  JobDetailsModule.cleanState();
  JobListModule.cleanState();
  CampaignDetailsModule.cleanState();
  CampaignListModule.cleanState();
  SpecOutDetailsModule.cleanState();
  SpecOutListModule.cleanState();
  LocationDetailsModule.cleanState();
  LocationListModule.cleanState();
  ReportsModule.cleanState();
  TimesheetListModule.cleanState();
  SysKBContentListModule.cleanState();
  OpportunityDetailsModule.cleanState();
  OpportunitiesListModule.cleanState();
  InactivityReportModule.cleanState();
  ManagementReportsModule.cleanState();
  ActivitiesListModule.cleanState();
  ActivityTimelineModule.cleanState();
  InvoiceDetailsModule.cleanState();
  InvoiceListModule.cleanState();
  KpiTargetsModule.cleanState();
  AgencyKBModule.cleanState();
  // messaging.cleanState accepts a retainConversationTopics flag; pass
  // false to fully reset.
  MessagingModule.cleanState(false);
  PayrollRunListModule.cleanState();
  PayrollRunDetailsModule.cleanState();
}
